import React, { useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Link } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { usePageTracking } from './hooks/usePageTracking';
import Hero from './components/Hero';
import AboutSection from './components/AboutSection';
import ServicesSection from './components/ServicesSection';
import ServiceOverview from './components/ServiceOverview';
import BenefitsFeatures from './components/BenefitsFeatures';
import HowItWorks from './components/HowItWorks';
import PoweredByTechnology from './components/PoweredByTechnology';
import WhatWeOffer from './components/WhatWeOffer';
import buffaloWildWingsLogo from './assets/Buffalo-Wild-Wings-Logo.png';
import arbysLogo from './assets/Arbys_logo.png';
import moesLogo from './assets/Moes_Logo_Diamond.png';
import sonicLogo from './assets/SONIC_New_Logo_2020.png';
import auntieAnnesLogo from './assets/Auntie-Annes®-on-white-1.png';
import uxpilotLogo from './assets/uxpilot.svg';
import xfinityLogo from './assets/Xfinity-Logo.png';
import awsLogo from './assets/Amazon_Web_Services_Logo.png';
import zohoLogo from './assets/zoho.png';
import checkoutChampLogo from './assets/checkoutchamp.webp';

// Import existing components and pages
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import Home from './pages/Home';
import About from './pages/about/About';
import CaseStudies from './pages/case-studies/CaseStudies';
import Blog from './pages/blog/Blog';
import BlogPost from './pages/blog/BlogPost';
import Contact from './pages/contact/Contact';
import Login from './pages/login/Login';
import SignUp from './pages/signup/SignUp';
import ForgotPassword from './pages/forgot-password/ForgotPassword';
import Profile from './pages/profile/Profile';
import ProtectedRoute from './components/ProtectedRoute';
import SubscriptionCheck from './components/SubscriptionCheck';
import EcommerceSolutions from './pages/services/EcommerceSolutions';
import RestaurantApps from './pages/services/RestaurantApps';
import ServiceBusinessSoftware from './pages/services/ServiceBusinessSoftware';
import { AuthProvider } from './contexts/AuthContext';
import { SubscriptionProvider } from './contexts/SubscriptionContext';
import { NotificationProvider } from './contexts/NotificationContext';
import Dashboard from './pages/dashboard/Dashboard';
import CreateRequest from './pages/dashboard/CreateRequest';
import AuthenticatedLayout from './layouts/AuthenticatedLayout';
import SidebarLayout from './layouts/SidebarLayout';
import Brands from './pages/dashboard/Brands';
import CreateBrand from './pages/dashboard/CreateBrand';
import RequestForm from './pages/dashboard/RequestForm';
import RequestDetails from './pages/dashboard/RequestDetails';
import AdminLayout from './layouts/AdminLayout';
import AdminDashboard from './pages/admin/Dashboard';
import AdminRequestDetails from './pages/admin/RequestDetails';
import TeamManagement from './pages/admin/TeamManagement';
import OnboardingLayout from './pages/onboarding/OnboardingLayout';
import OnboardingLanding from './pages/onboarding/Landing';
import OnboardingSignUp from './pages/onboarding/SignUp';
import OnboardingChoosePlan from './pages/onboarding/ChoosePlan';
import OnboardingWelcome from './pages/onboarding/Welcome';
import OnboardingFirstRequest from './pages/onboarding/FirstRequest';
import OnboardingConfirmation from './pages/onboarding/Confirmation';
import Pricing from './pages/Pricing';
import SubscriptionPage from './pages/subscription/SubscriptionPage';
import Development from './pages/services/Development';
import UIDesign from './pages/services/UIDesign';
import Branding from './pages/services/Branding';
import GraphicDesign from './pages/services/GraphicDesign';
import SuccessPage from './pages/SuccessPage';
import { PageTitle } from './components/PageTitle';
import { getHreflangForPage } from './config/international';
import AuthCallback from './pages/auth/AuthCallback';
import TermsAndConditions from './pages/legal/TermsAndConditions';
import PrivacyPolicy from './pages/legal/PrivacyPolicy';

// Component to handle page tracking inside Router context
const AppContent: React.FC = () => {
  const [pricingTab, setPricingTab] = useState<'monthly' | 'annual'>('monthly');
  
  // Track page views - now inside Router context
  usePageTracking();
  
  // Add debugging to track component lifecycle
  React.useEffect(() => {
    console.log('🚀 AppContent mounted');
    
    // Add visibility change listener to debug tab switching
    const handleVisibilityChange = () => {
      console.log('👁️ Visibility changed:', {
        hidden: document.hidden,
        visibilityState: document.visibilityState,
        timestamp: new Date().toISOString()
      });
    };
    
    // Add page focus/blur listeners
    const handlePageShow = () => {
      console.log('📱 Page shown/focused');
    };
    
    const handlePageHide = () => {
      console.log('📱 Page hidden/blurred');
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('pageshow', handlePageShow);
    window.addEventListener('pagehide', handlePageHide);
    
    return () => {
      console.log('🚀 AppContent unmounting');
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('pageshow', handlePageShow);
      window.removeEventListener('pagehide', handlePageHide);
    };
  }, []);
  
  return (
    <div className="min-h-screen bg-white">
      {/* Only show Navbar on public (non-dashboard) routes */}
      <Routes>
        {/* Test Route - Remove in production */}
        <Route path="/test-auth" element={
          <ProtectedRoute>
            <div className="min-h-screen flex items-center justify-center">
              <div className="text-center">
                <h1 className="text-2xl font-bold mb-4">Authentication Test</h1>
                <p className="text-gray-600">If you can see this, authentication is working!</p>
              </div>
            </div>
          </ProtectedRoute>
        } />
        
        {/* Admin Routes */}
        <Route path="/admin" element={
          <ProtectedRoute requireAdmin>
            <AdminLayout>
              <AdminDashboard />
            </AdminLayout>
          </ProtectedRoute>
        } />
        <Route path="/admin/requests" element={
          <ProtectedRoute requireAdmin>
            <AdminLayout>
              <AdminDashboard />
            </AdminLayout>
          </ProtectedRoute>
        } />
        <Route path="/admin/team" element={
          <ProtectedRoute requireAdmin>
            <AdminLayout>
              <TeamManagement />
            </AdminLayout>
          </ProtectedRoute>
        } />
        <Route path="/admin/settings" element={
          <ProtectedRoute requireAdmin>
            <AdminLayout>
              <div className="p-6">
                <h1 className="text-2xl font-bold mb-4">Admin Settings</h1>
                <p className="text-gray-600">Admin settings coming soon...</p>
              </div>
            </AdminLayout>
          </ProtectedRoute>
        } />
        <Route path="/admin/requests/:id" element={
          <ProtectedRoute requireAdmin>
            <AdminLayout>
              <AdminRequestDetails />
            </AdminLayout>
          </ProtectedRoute>
        } />
        <Route path="/dashboard" element={
          <ProtectedRoute>
            <SubscriptionCheck>
              <SidebarLayout>
                <Dashboard />
              </SidebarLayout>
            </SubscriptionCheck>
          </ProtectedRoute>
        } />
        <Route path="/dashboard/create" element={
          <ProtectedRoute>
            <SubscriptionCheck>
              <SidebarLayout>
                <CreateRequest />
              </SidebarLayout>
            </SubscriptionCheck>
          </ProtectedRoute>
        } />
        <Route path="/dashboard/brands" element={
          <ProtectedRoute>
            <SubscriptionCheck>
              <SidebarLayout>
                <Brands />
              </SidebarLayout>
            </SubscriptionCheck>
          </ProtectedRoute>
        } />
        <Route path="/dashboard/brands/create" element={
          <ProtectedRoute>
            <SubscriptionCheck>
              <SidebarLayout>
                <CreateBrand />
              </SidebarLayout>
            </SubscriptionCheck>
          </ProtectedRoute>
        } />
        <Route path="/dashboard/requests/:id" element={
          <ProtectedRoute>
            <SubscriptionCheck>
              <SidebarLayout>
                <RequestDetails />
              </SidebarLayout>
            </SubscriptionCheck>
          </ProtectedRoute>
        } />
        <Route path="/dashboard/requests/create/:type" element={
          <ProtectedRoute>
            <SubscriptionCheck>
              <SidebarLayout>
                <RequestForm />
              </SidebarLayout>
            </SubscriptionCheck>
          </ProtectedRoute>
        } />
        {/* Subscription Route */}
        <Route path="/subscription" element={
          <ProtectedRoute>
            <SubscriptionPage />
          </ProtectedRoute>
        } />
        {/* All other routes show Navbar */}
        <Route path="*" element={
          <>
            <Navbar />
            <Routes>
              <Route path="/" element={
                <>
                  <PageTitle 
                    title="CodeSplash - Custom Software Development Company | Web Apps, Mobile Apps, Business Software"
                    description="CodeSplash is a custom software development company in Atlanta, GA. We build web apps, mobile apps, and business software solutions for growing companies."
                    canonicalUrl="https://codesplashdesigns.com"
                    hreflang={getHreflangForPage('/')}
                    language="en"
                  />
                  <Hero />
                  <div className="py-12 bg-gray-50">
                    <div className="container mx-auto px-4">
                      <h3 className="text-2xl font-bold text-gray-900 mb-8 text-center">
                        WE WORKED WITH GLOBAL LARGEST BRANDS
                      </h3>
                      <div className="flex justify-center items-center gap-12 flex-wrap">
                        <img 
                          src={checkoutChampLogo} 
                          alt="Checkout Champ" 
                          className="h-16 w-auto grayscale hover:grayscale-0 transition-all duration-300"
                        />
                        <img 
                          src={zohoLogo} 
                          alt="Zoho" 
                          className="h-24 w-auto grayscale hover:grayscale-0 transition-all duration-300"
                        />
                        <img 
                          src={awsLogo} 
                          alt="AWS" 
                          className="h-10 w-auto grayscale hover:grayscale-0 transition-all duration-300"
                        />
                        <img 
                          src={xfinityLogo} 
                          alt="xfinity" 
                          className="h-10 w-auto grayscale hover:grayscale-0 transition-all duration-300"
                        />
                        <img 
                          src={uxpilotLogo} 
                          alt="UX Pilot" 
                          className="h-8 w-auto grayscale hover:grayscale-0 transition-all duration-300"
                        />
                      </div>
                    </div>
                  </div>
                  <HowItWorks />
                  <PoweredByTechnology />
                  <WhatWeOffer />
                  {/* Pricing Section with Tabs */}
                  <section className="py-16 bg-white">
                    <div className="container mx-auto px-4">
                      <h2 className="text-3xl font-bold text-center mb-10">Pricing</h2>
                      <div className="flex justify-center mb-8">
                        <button
                          className={`px-6 py-2 rounded-l font-semibold border-t border-l border-b max-w-32 ${pricingTab === 'monthly' ? 'bg-primary text-white' : 'bg-gray-100 text-gray-800'}`}
                          onClick={() => setPricingTab('monthly')}
                        >
                          <span className="px-8">Monthly</span>
                        </button>
                        <button
                          className={`px-6 py-2 rounded-r font-semibold border-t border-r border-b max-w-32 ${pricingTab === 'annual' ? 'bg-primary text-white' : 'bg-gray-100 text-gray-800'}`}
                          onClick={() => setPricingTab('annual')}
                        >
                          Annual <span className="ml-1 text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full">(20% off)</span>
                        </button>
                      </div>
                      {pricingTab === 'monthly' ? (
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
                          <div className="rounded-3xl bg-[#f7fbff] shadow p-8 flex flex-col border border-gray-200">
                            <h3 className="text-2xl font-bold mb-2 text-black font-sans">Graphics</h3>
                            <div className="flex items-end mb-2">
                              <span className="text-5xl font-bold text-primary font-sans">$440</span>
                              <span className="text-gray-400 text-xl font-semibold ml-1">/month</span>
                            </div>
                            <div className="mb-4 text-base">
                              <span className="font-bold text-primary">1 Active Task</span> <span className="text-gray-700">at a time</span>
                            </div>
                            <hr className="my-4" />
                            <div className="mb-2 font-bold text-lg">Services</div>
                            <ul className="mb-4 space-y-2">
                              <li className="flex items-center text-base text-black">
                                <span className="material-icons text-primary mr-2 text-lg">check_circle</span>
                                Graphic Design
                              </li>
                              <li className="flex items-center text-base text-black">
                                <span className="material-icons text-primary mr-2 text-lg">check_circle</span>
                                Illustrations
                              </li>
                              <li className="flex items-center text-base text-gray-400 line-through">
                                <span className="material-icons mr-2 text-lg">cancel</span>
                                Web & App UI Design
                              </li>
                              <li className="flex items-center text-base text-gray-400 line-through">
                                <span className="material-icons mr-2 text-lg">cancel</span>
                                Motion Graphics
                              </li>
                              <li className="flex items-center text-base text-gray-400 line-through">
                                <span className="material-icons mr-2 text-lg">cancel</span>
                                Video Editing
                              </li>
                              <li className="flex items-center text-base text-gray-400 line-through">
                                <span className="material-icons mr-2 text-lg">cancel</span>
                                Website Development
                              </li>
                            </ul>
                            <hr className="my-4" />
                            <div className="flex items-center text-base">
                              <span className="text-primary font-semibold mr-1">24–48 hrs</span>
                              <span className="text-gray-700">Delivery Time</span>
                              <span className="material-icons text-gray-400 ml-2 text-base" title="Most requests delivered in 1–2 business days">info</span>
                            </div>
                            <Link to="/signup" className="mt-6 bg-primary text-white px-6 py-3 rounded-lg font-semibold text-center hover:bg-primary/90 transition-colors">
                              Get Started Now
                            </Link>
                          </div>
                          <div className="rounded-3xl bg-[#f7fbff] shadow p-8 flex flex-col border border-gray-200">
                            <h3 className="text-2xl font-bold mb-2 text-black font-sans">Pro</h3>
                            <div className="flex items-end mb-2">
                              <span className="text-5xl font-bold text-primary font-sans">$590</span>
                              <span className="text-gray-400 text-xl font-semibold ml-1">/month</span>
                            </div>
                            <div className="mb-4 text-base">
                              <span className="font-bold text-primary">1 Active Task</span> <span className="text-gray-700">at a time</span>
                            </div>
                            <hr className="my-4" />
                            <div className="mb-2 font-bold text-lg">Services</div>
                            <ul className="mb-4 space-y-2">
                              <li className="flex items-center text-base text-black">
                                <span className="material-icons text-primary mr-2 text-lg">check_circle</span>
                                Graphic Design
                              </li>
                              <li className="flex items-center text-base text-black">
                                <span className="material-icons text-primary mr-2 text-lg">check_circle</span>
                                Illustrations
                              </li>
                              <li className="flex items-center text-base text-black">
                                <span className="material-icons text-primary mr-2 text-lg">check_circle</span>
                                Web & App UI Design
                              </li>
                              <li className="flex items-center text-base text-black">
                                <span className="material-icons text-primary mr-2 text-lg">check_circle</span>
                                Motion Graphics
                              </li>
                              <li className="flex items-center text-base text-black">
                                <span className="material-icons text-primary mr-2 text-lg">check_circle</span>
                                Video Editing
                              </li>
                              <li className="flex items-center text-base text-gray-400 line-through">
                                <span className="material-icons mr-2 text-lg">cancel</span>
                                Website Development
                              </li>
                            </ul>
                            <hr className="my-4" />
                            <div className="flex items-center text-base">
                              <span className="text-primary font-semibold mr-1">24–48 hrs</span>
                              <span className="text-gray-700">Delivery Time</span>
                              <span className="material-icons text-gray-400 ml-2 text-base" title="Most requests delivered in 1–2 business days">info</span>
                            </div>
                            <Link to="/signup" className="mt-6 bg-primary text-white px-6 py-3 rounded-lg font-semibold text-center hover:bg-primary/90 transition-colors">
                              Get Started Now
                            </Link>
                          </div>
                          <div className="rounded-3xl bg-[#f7fbff] shadow p-8 flex flex-col border-2 border-primary">
                            <h3 className="text-2xl font-bold mb-2 text-black font-sans">Development</h3>
                            <div className="flex items-end mb-2">
                              <span className="text-5xl font-bold text-primary font-sans">$1390</span>
                              <span className="text-gray-400 text-xl font-semibold ml-1">/month</span>
                            </div>
                            <div className="mb-4 text-base">
                              <span className="font-bold text-primary">1 Active Task</span> <span className="text-gray-700">at a time</span>
                            </div>
                            <hr className="my-4" />
                            <div className="mb-2 font-bold text-lg">Services</div>
                            <ul className="mb-4 space-y-2">
                              <li className="flex items-center text-base text-black">
                                <span className="material-icons text-primary mr-2 text-lg">check_circle</span>
                                Graphic Design
                              </li>
                              <li className="flex items-center text-base text-black">
                                <span className="material-icons text-primary mr-2 text-lg">check_circle</span>
                                Illustrations
                              </li>
                              <li className="flex items-center text-base text-black">
                                <span className="material-icons text-primary mr-2 text-lg">check_circle</span>
                                Web & App UI Design
                              </li>
                              <li className="flex items-center text-base text-black">
                                <span className="material-icons text-primary mr-2 text-lg">check_circle</span>
                                Motion Graphics
                              </li>
                              <li className="flex items-center text-base text-black">
                                <span className="material-icons text-primary mr-2 text-lg">check_circle</span>
                                Video Editing
                              </li>
                              <li className="flex items-center text-base text-black">
                                <span className="material-icons text-primary mr-2 text-lg">check_circle</span>
                                Website Development
                              </li>
                            </ul>
                            <hr className="my-4" />
                            <div className="flex items-center text-base">
                              <span className="text-primary font-semibold mr-1">24–48 hrs</span>
                              <span className="text-gray-700">Delivery Time</span>
                              <span className="material-icons text-gray-400 ml-2 text-base" title="Most requests delivered in 1–2 business days">info</span>
                            </div>
                            <Link to="/signup" className="mt-6 bg-primary text-white px-6 py-3 rounded-lg font-semibold text-center hover:bg-primary/90 transition-colors">
                              Get Started Now
                            </Link>
                          </div>
                        </div>
                      ) : (
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
                          <div className="rounded-3xl bg-[#f7fbff] shadow p-8 flex flex-col border border-gray-200">
                            <h3 className="text-2xl font-bold mb-2 text-black font-sans">Graphics</h3>
                            <div className="flex items-end mb-2">
                              <span className="text-5xl font-bold text-primary font-sans">$4,752</span>
                              <span className="text-gray-400 text-xl font-semibold ml-1">/year</span>
                            </div>
                            <div className="mb-4 text-base">
                              <span className="font-bold text-primary">1 Active Task</span> <span className="text-gray-700">at a time</span>
                            </div>
                            <hr className="my-4" />
                            <div className="mb-2 font-bold text-lg">Services</div>
                            <ul className="mb-4 space-y-2">
                              <li className="flex items-center text-base text-black">
                                <span className="material-icons text-primary mr-2 text-lg">check_circle</span>
                                Graphic Design
                              </li>
                              <li className="flex items-center text-base text-black">
                                <span className="material-icons text-primary mr-2 text-lg">check_circle</span>
                                Illustrations
                              </li>
                              <li className="flex items-center text-base text-gray-400 line-through">
                                <span className="material-icons mr-2 text-lg">cancel</span>
                                Web & App UI Design
                              </li>
                              <li className="flex items-center text-base text-gray-400 line-through">
                                <span className="material-icons mr-2 text-lg">cancel</span>
                                Motion Graphics
                              </li>
                              <li className="flex items-center text-base text-gray-400 line-through">
                                <span className="material-icons mr-2 text-lg">cancel</span>
                                Video Editing
                              </li>
                              <li className="flex items-center text-base text-gray-400 line-through">
                                <span className="material-icons mr-2 text-lg">cancel</span>
                                Website Development
                              </li>
                            </ul>
                            <hr className="my-4" />
                            <div className="flex items-center text-base">
                              <span className="text-primary font-semibold mr-1">24–48 hrs</span>
                              <span className="text-gray-700">Delivery Time</span>
                              <span className="material-icons text-gray-400 ml-2 text-base" title="Most requests delivered in 1–2 business days">info</span>
                            </div>
                            <Link to="/signup" className="mt-6 bg-primary text-white px-6 py-3 rounded-lg font-semibold text-center hover:bg-primary/90 transition-colors">
                              Get Started Now
                            </Link>
                          </div>
                          <div className="rounded-3xl bg-[#f7fbff] shadow p-8 flex flex-col border border-gray-200">
                            <h3 className="text-2xl font-bold mb-2 text-black font-sans">Experience</h3>
                            <div className="flex items-end mb-2">
                              <span className="text-5xl font-bold text-primary font-sans">$6,372</span>
                              <span className="text-gray-400 text-xl font-semibold ml-1">/year</span>
                            </div>
                            <div className="mb-4 text-base">
                              <span className="font-bold text-primary">1 Active Task</span> <span className="text-gray-700">at a time</span>
                            </div>
                            <hr className="my-4" />
                            <div className="mb-2 font-bold text-lg">Services</div>
                            <ul className="mb-4 space-y-2">
                              <li className="flex items-center text-base text-black">
                                <span className="material-icons text-primary mr-2 text-lg">check_circle</span>
                                Graphic Design
                              </li>
                              <li className="flex items-center text-base text-black">
                                <span className="material-icons text-primary mr-2 text-lg">check_circle</span>
                                Illustrations
                              </li>
                              <li className="flex items-center text-base text-black">
                                <span className="material-icons text-primary mr-2 text-lg">check_circle</span>
                                Web & App UI Design
                              </li>
                              <li className="flex items-center text-base text-black">
                                <span className="material-icons text-primary mr-2 text-lg">check_circle</span>
                                Motion Graphics
                              </li>
                              <li className="flex items-center text-base text-black">
                                <span className="material-icons text-primary mr-2 text-lg">check_circle</span>
                                Video Editing
                              </li>
                              <li className="flex items-center text-base text-gray-400 line-through">
                                <span className="material-icons mr-2 text-lg">cancel</span>
                                Website Development
                              </li>
                            </ul>
                            <hr className="my-4" />
                            <div className="flex items-center text-base">
                              <span className="text-primary font-semibold mr-1">24–48 hrs</span>
                              <span className="text-gray-700">Delivery Time</span>
                              <span className="material-icons text-gray-400 ml-2 text-base" title="Most requests delivered in 1–2 business days">info</span>
                            </div>
                            <Link to="/signup" className="mt-6 bg-primary text-white px-6 py-3 rounded-lg font-semibold text-center hover:bg-primary/90 transition-colors">
                              Get Started Now
                            </Link>
                          </div>
                          <div className="rounded-3xl bg-[#f7fbff] shadow p-8 flex flex-col border-2 border-primary">
                            <h3 className="text-2xl font-bold mb-2 text-black font-sans">Build</h3>
                            <div className="flex items-end mb-2">
                              <span className="text-5xl font-bold text-primary font-sans">$15,012</span>
                              <span className="text-gray-400 text-xl font-semibold ml-1">/year</span>
                            </div>
                            <div className="mb-4 text-base">
                              <span className="font-bold text-primary">1 Active Task</span> <span className="text-gray-700">at a time</span>
                            </div>
                            <hr className="my-4" />
                            <div className="mb-2 font-bold text-lg">Services</div>
                            <ul className="mb-4 space-y-2">
                              <li className="flex items-center text-base text-black">
                                <span className="material-icons text-primary mr-2 text-lg">check_circle</span>
                                Graphic Design
                              </li>
                              <li className="flex items-center text-base text-black">
                                <span className="material-icons text-primary mr-2 text-lg">check_circle</span>
                                Illustrations
                              </li>
                              <li className="flex items-center text-base text-black">
                                <span className="material-icons text-primary mr-2 text-lg">check_circle</span>
                                Web & App UI Design
                              </li>
                              <li className="flex items-center text-base text-black">
                                <span className="material-icons text-primary mr-2 text-lg">check_circle</span>
                                Motion Graphics
                              </li>
                              <li className="flex items-center text-base text-black">
                                <span className="material-icons text-primary mr-2 text-lg">check_circle</span>
                                Video Editing
                              </li>
                              <li className="flex items-center text-base text-black">
                                <span className="material-icons text-primary mr-2 text-lg">check_circle</span>
                                Website Development
                              </li>
                            </ul>
                            <hr className="my-4" />
                            <div className="flex items-center text-base">
                              <span className="text-primary font-semibold mr-1">24–48 hrs</span>
                              <span className="text-gray-700">Delivery Time</span>
                              <span className="material-icons text-gray-400 ml-2 text-base" title="Most requests delivered in 1–2 business days">info</span>
                            </div>
                            <Link to="/signup" className="mt-6 bg-primary text-white px-6 py-3 rounded-lg font-semibold text-center hover:bg-primary/90 transition-colors">
                              Get Started Now
                            </Link>
                          </div>
                        </div>
                      )}
                    </div>
                  </section>
                  <AboutSection />
                </>
              } />
              <Route path="/about" element={<About />} />
              <Route path="/case-studies" element={<CaseStudies />} />
              <Route path="/blog" element={<Blog />} />
              <Route path="/blog/:slug" element={<BlogPost />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/login" element={<Login />} />
              <Route path="/signup" element={<SignUp />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/profile" element={<Profile />} />
              <Route path="/pricing" element={<Pricing />} />
              <Route path="/services/ecommerce-solutions" element={<EcommerceSolutions />} />
              <Route path="/services/restaurant-apps" element={<RestaurantApps />} />
              <Route path="/services/business-software" element={<ServiceBusinessSoftware />} />
              <Route path="/services/development" element={<Development />} />
              <Route path="/services/ui-design" element={<UIDesign />} />
              <Route path="/services/branding" element={<Branding />} />
              <Route path="/services/graphic-design" element={<GraphicDesign />} />
              <Route path="/onboarding" element={<OnboardingLayout />} />
              <Route path="/onboarding/landing" element={<OnboardingLanding />} />
              <Route path="/onboarding/signup" element={<OnboardingSignUp />} />
              <Route path="/onboarding/plan" element={<OnboardingChoosePlan />} />
              <Route path="/onboarding/welcome" element={<OnboardingWelcome />} />
              <Route path="/onboarding/first-request" element={<OnboardingFirstRequest />} />
              <Route path="/onboarding/confirmation" element={<OnboardingConfirmation />} />
              <Route path="/success" element={<SuccessPage />} />
              <Route path="/auth-callback" element={<AuthCallback />} />
              <Route path="/terms-and-conditions" element={<TermsAndConditions />} />
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            </Routes>
            <Footer />
          </>
        } />
      </Routes>
    </div>
  );
};

function App() {
  return (
    <HelmetProvider>
      <Router>
        <AuthProvider>
          <SubscriptionProvider>
            <NotificationProvider>
              <AppContent />
            </NotificationProvider>
          </SubscriptionProvider>
        </AuthProvider>
      </Router>
    </HelmetProvider>
  );
}

export default App;
